import { defineStore } from "pinia";
import {
  signUp,
  signIn,
  signOut,
  fetchUserAttributes,
  confirmSignUp,
  resendSignUpCode,
  confirmResetPassword,
  resetPassword,
} from "aws-amplify/auth";
import router from "@/router";
import { ApiService } from "@/services/api-service";
import { apis } from "@/services/constant-services";
import { cleanupEventListeners } from "@/utils/event-listeners";

// Safe JSON parse helper
const safeJSONParse = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    console.error(`Error parsing ${key} from sessionStorage:`, error);
    sessionStorage.removeItem(key); // Remove corrupted data
    return null;
  }
};

export const useUserStore = defineStore("user", {
  state: () => ({
    user: safeJSONParse("user"),
    dashboardData: safeJSONParse("dashboardData"),
    goalSummary: safeJSONParse("goalSummary"),
    financialStatement: safeJSONParse("financialStatement"),
    screenerData: safeJSONParse("screenerData"),
    portfolioAnalyticsData: safeJSONParse("portfolioAnalyticsData"),
    riskAssessmentData: null,
    isAuthenticated: false,
    currentAuthView: "signin",
    forgotPasswordStep: "request",
    userTab: "Dashboard",
    isAuthLoading: false,
    isSignoutLoading: false,
    isDashboardLoading: false,
    isRiskAssessmentLoading: false,
    isProfileCheckLoading: false, // NEW: Loading state for profile check during initial login
    alert: null,
    portfolioAttributionData: null,
    sessionValidated: false, // NEW: Track if current session is validated through proper login flow
  }),

  actions: {
    setAuthLoading(flag) {
      this.isAuthLoading = flag;
    },

    setDashboardLoading(flag) {
      this.isDashboardLoading = flag;
    },

    fireAlert(type, message) {
      // Safely convert values to strings, handling objects and error instances
      let typeStr = '';
      let messageStr = '';

      try {
        if (type !== null && type !== undefined) {
          typeStr = typeof type === 'object' ? String(type.toString()) : String(type);
        }

        if (message !== null && message !== undefined) {
          // Handle Error objects specially
          if (message instanceof Error) {
            messageStr = message.message || String(message);
          } else if (typeof message === 'object') {
            messageStr = message.message || JSON.stringify(message) || String(message);
          } else {
            messageStr = String(message);
          }
        }
      } catch (conversionError) {
        console.error('Error converting alert values:', conversionError);
        typeStr = 'Error';
        messageStr = 'An error occurred';
      }

      this.alert = {
        summary: typeStr,
        severity: typeStr.toLowerCase(),
        detail: messageStr,
      };
    },

    navigate(routeName) {
      router.push({ name: routeName });
    },

    setAuthView(view) {
      this.currentAuthView = view;
    },

    setTabView(view) {
      this.userTab = view;
    },

    clearsession() {
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("dashboardData");
      sessionStorage.removeItem("goalSummary");
      sessionStorage.removeItem("financialStatement");
      sessionStorage.removeItem("screenerData");
      sessionStorage.removeItem("portfolioAnalyticsData");
      sessionStorage.removeItem("sessionValidated"); // NEW: Clear session validation flag
      // Clear benchmark data cache from localStorage
      localStorage.removeItem("sector_benchmark_data");
      // Clear portfolio attribution cache from localStorage
      const attributionKeys = Object.keys(localStorage).filter(key =>
        key.startsWith("portfolio_attribution_data")
      );
      attributionKeys.forEach(key => localStorage.removeItem(key));

      // Clear SIP portfolio allocation cache from localStorage (if any stored with customer ID)
      const sipAllocationKeys = Object.keys(localStorage).filter(key =>
        key.startsWith("sip_portfolio_allocation")
      );
      sipAllocationKeys.forEach(key => localStorage.removeItem(key));

      // Reset session validation flag
      this.sessionValidated = false;
    },

    //Auth API Functions

    async userSignIn(email, password) {
      this.setAuthLoading(true);
      this.error = null;
      try {
        await signIn({
          username: email,
          password,
        });
        this.setAuthLoading(false);
        // NEW: Mark session as validated through proper login flow
        this.sessionValidated = true;
        sessionStorage.setItem("sessionValidated", "true");
        this.setUserInSession();
      } catch (error) {
        this.setAuthLoading(false);

        // Parse Cognito error and show user-friendly message
        let errorMessage = "Login failed. Please try again.";

        console.error('Login error:', error);

        // Check error name/code for specific Cognito errors
        const errorName = error.name || error.code || '';
        const errorMsg = error.message || '';

        if (errorName === 'UserNotFoundException' || errorMsg.includes('User does not exist')) {
          errorMessage = "No account found with this email address. Please check your email or sign up.";
        } else if (errorName === 'NotAuthorizedException') {
          if (errorMsg.includes('Incorrect username or password')) {
            errorMessage = "Incorrect email or password. Please try again.";
          } else if (errorMsg.includes('User is disabled')) {
            errorMessage = "Your account has been disabled. Please contact support.";
          } else if (errorMsg.includes('Password attempts exceeded')) {
            errorMessage = "Too many failed login attempts. Please try again later or reset your password.";
          } else {
            errorMessage = "Incorrect email or password. Please try again.";
          }
        } else if (errorName === 'UserNotConfirmedException' || errorMsg.includes('User is not confirmed')) {
          errorMessage = "Your email is not verified. Please check your inbox for the verification code.";
          // Optionally auto-navigate to verify screen
          this.setAuthView('verify-signup');
        } else if (errorName === 'PasswordResetRequiredException') {
          errorMessage = "Password reset is required for your account. Please reset your password.";
          this.forgotPasswordStep = 'request';
          this.setAuthView('forgot-password');
        } else if (errorName === 'TooManyRequestsException' || errorMsg.includes('Too many requests')) {
          errorMessage = "Too many login attempts. Please wait a few minutes and try again.";
        } else if (errorName === 'InvalidPasswordException' || errorMsg.includes('password')) {
          errorMessage = "Invalid password format. Password must meet security requirements.";
        } else if (errorMsg.includes('Network error') || errorMsg.includes('fetch')) {
          errorMessage = "Network error. Please check your internet connection and try again.";
        }

        this.fireAlert("Error", errorMessage);
        throw error;
      }
    },

    async userSignUp(email, password, confirmPassword, username) {
      this.setAuthLoading(true);
      this.error = null;
      if (password !== confirmPassword) {
        this.fireAlert("Error", "Passwords do not match");
        this.setAuthLoading(false);
        return;
      }
      try {
        await signUp({
          username: email,
          password,
          options: {
            userAttributes: {
              email,
              preferred_username: username,
            },
          },
        });
        this.setAuthLoading(false);
        //this.fireAlert("Success", "User Registered");
        this.setAuthView("verify-signup");
      } catch (error) {
        this.setAuthLoading(false);
        this.fireAlert("Error", error.message);
        throw error;
      }
    },

    async userSignUpVerification(email, code) {
      try {
        this.setAuthLoading(true);
        await confirmSignUp({
          username: email,
          confirmationCode: code,
        });
        this.setAuthLoading(false);
        this.fireAlert("Success", "Account verified successfully!");
        this.setAuthView("signin");
      } catch (error) {
        this.setAuthLoading(false);
        this.fireAlert("Error", error.message);
      }
    },

    async resendSignUpVerificationCode(email) {
      try {
        await resendSignUpCode({ username: email }).then(() => {
          this.setAuthLoading(false);
          this.fireAlert("Success", "New verification code sent!");
        });
      } catch (error) {
        this.fireAlert("Error", error.message);
      }
    },

    async handleForgotPassword(
      email,
      newPassword,
      confirmNewPassword,
      resetCode
    ) {
      try {
        this.setAuthLoading(true);
        if (this.forgotPasswordStep === "request") {
          await resetPassword({ username: email });
          this.setAuthLoading(false);
          this.forgotPasswordStep = "confirm";
          this.fireAlert("Success", "Reset code sent!");
        } else if (this.forgotPasswordStep === "confirm") {
          if (newPassword !== confirmNewPassword) {
            this.fireAlert("Error", "Passwords do not match");
            this.setAuthLoading(false);
            return;
          }
          await confirmResetPassword({
            username: email,
            confirmationCode: resetCode,
            newPassword: newPassword,
          });
          this.fireAlert("Success", "Password Reset Successfull!");
          this.setAuthLoading(false);
          this.setAuthView("signin");
        }
      } catch (error) {
        this.fireAlert("Error", error.message);
        this.setAuthLoading(false);
      }
    },

    async resendPasswordResetCode(email) {
      this.setAuthLoading(true);
      try {
        await resetPassword({ username: email });
        this.fireAlert("Success", "New reset code sent");
        this.setAuthLoading(false);
      } catch (error) {
        this.fireAlert("Error", error.message);
        this.setAuthLoading(false);
      }
    },

    async handleLogout() {
      this.isSignoutLoading = true;
      try {
        // Sign out from AWS Cognito first
        try {
          await signOut();
        } catch (signOutError) {
          console.warn('AWS Cognito signout failed:', signOutError);
          // Continue with cleanup even if AWS signout fails
        }

        // Clear session storage immediately
        this.clearsession();

        // Clear all reactive state after signout
        this.user = null;
        this.dashboardData = null;
        this.goalSummary = null;
        this.financialStatement = null;
        this.screenerData = null;
        this.portfolioAnalyticsData = null;
        this.riskAssessmentData = null;
        this.portfolioAttributionData = null;

        // Reset authentication state
        this.isAuthenticated = false;
        this.sessionValidated = false; // NEW: Reset session validation
        this.currentAuthView = "signin";
        this.forgotPasswordStep = "request";
        this.userTab = "Dashboard";

        // Clear any pending alerts
        this.alert = null;

        // Navigate to home with error handling
        try {
          if (router && typeof router.push === 'function') {
            await router.push("/");
          } else {
            window.location.href = "/";
          }
        } catch (navigationError) {
          console.warn('Router navigation failed, using window.location:', navigationError);
          window.location.href = "/";
        }

      } catch (error) {
        console.error("Logout error:", error);
        // Even if logout fails, clear local state and redirect
        this.clearsession();
        this.isAuthenticated = false;
        try {
          window.location.href = "/";
        } catch (redirectError) {
          console.error("Failed to redirect after logout:", redirectError);
        }
      } finally {
        this.isSignoutLoading = false;
      }
    },

    //Dashboard API Functions

    async setUserInSession() {
      this.setAuthLoading(true);
      this.isProfileCheckLoading = true; // Start profile check loading

      const userAttributes = await fetchUserAttributes();
      let user = {
        userId: userAttributes.sub,
        email: userAttributes.email,
        username: userAttributes.preferred_username,
        isVerified: userAttributes.email_verified,
        isAuthenticated: true,
      };
      this.user = user;
      sessionStorage.setItem("user", JSON.stringify(user));

      // Navigate to dashboard first
      if (window.location.pathname !== "/dashboard") {
        this.navigate("Dashboard");
      }

      // Turn off auth loading but keep profile check loading active
      this.setAuthLoading(false);

      // Load dashboard data and determine appropriate tab
      // Profile check loading will be turned off in loadDashboardData
      await this.loadDashboardData(userAttributes.email);
      //this.fireAlert("Success", "Welcome to Asset Nivesh");
    },

    async loadDashboardData(payload, skipNavigation = false) {
      this.setDashboardLoading(true);
      try {
        const response = await ApiService.postData(
          apis.getPortfilioDashboardData,
          { userEmail: payload }
        );

        // Handle nested dashboard data structure
        const dashboardData = response.data.dashboard || response.data;
        console.log('Dashboard data snapshot at line 396:', JSON.parse(JSON.stringify(dashboardData)));

        // Extract common identifiers
        let kycStatus = null;
        let paymentStatus = null;
        let docSigningStatus = null;
        const customerId = dashboardData.customerId;
        const phoneNumber = dashboardData.phoneNumber;
        const pan = dashboardData.pan;
        const isValidCustomerId = customerId &&
                                 customerId !== "None" &&
                                 customerId !== "" &&
                                 !isNaN(parseInt(customerId));

        // Check if statuses are already present in dashboardData
        const hasVerifiedStatuses = dashboardData.kycStatus === "Verified" &&
                                    dashboardData.agreementStatus === "Verified" &&
                                    dashboardData.paymentStatus === "Verified";

        if (hasVerifiedStatuses) {
          // Use statuses directly from dashboardData
          kycStatus = dashboardData.kycStatus;
          docSigningStatus = dashboardData.agreementStatus;
          paymentStatus = dashboardData.paymentStatus;
          console.log('Using verified statuses from dashboardData - KYC:', kycStatus, 'Agreement:', docSigningStatus, 'Payment:', paymentStatus);
        } else {
          // Fetch statuses from APIs if not already verified

          // Fetch KYC status first (must complete before payment)
          if (pan) {
          try {
            const kycPayload = {
              PAN: pan,
              CustomerIdentifier: dashboardData.PhoneNumber
            };
            const kycResponse = await ApiService.postData(apis.getKYCRequestStatus, kycPayload);
            console.log('KYC status response:', kycResponse.data);
            kycStatus = kycResponse?.data?.data?.Status || null;
            console.log('KYC status fetched:', kycStatus);
          } catch (error) {
            console.warn('Failed to fetch KYC status:', error);
          }
        }

        // Fetch doc signing status if KYC is done (after KYC check, before payment)
        if (isValidCustomerId && phoneNumber && (kycStatus === 'Done' || kycStatus === 'Verified')) {
          try {
            const docSigningPayload = {
              CustomerId: customerId,
              PhoneNumber: phoneNumber
            };
            const docSigningResponse = await ApiService.postData(apis.getDocSigningStatus, docSigningPayload);
            console.log('Doc signing status response:', docSigningResponse);
            // Access the correct path in response
            if (docSigningResponse.data?.data?.DocSigningStatus) {
              docSigningStatus = docSigningResponse.data.data.DocSigningStatus;
            } else if (docSigningResponse.data?.DocSigningStatus) {
              docSigningStatus = docSigningResponse.data.DocSigningStatus;
            } else {
              docSigningStatus = null;
            }

            // Treat "Not Initiated" as null
            if (docSigningStatus === 'Not Initiated') {
              docSigningStatus = null;
            }

            console.log('Doc signing status fetched:', docSigningStatus);

            // Sync with localStorage
            if (docSigningStatus === 'signed' || docSigningStatus === 'Verified') {
              localStorage.setItem(`agreement_signed_${customerId}`, 'true');
            } else {
              localStorage.removeItem(`agreement_signed_${customerId}`);
            }
          } catch (error) {
            console.warn('Failed to fetch doc signing status:', error);
          }
        }

        // Fetch payment status if customerId exists (after KYC check)
        if (isValidCustomerId && phoneNumber) {
          try {
            const paymentPayload = {
              CustomerId: customerId,
              PhoneNumber: phoneNumber
            };
            const paymentResponse = await ApiService.postData(apis.getCustomerPaymentStatus, paymentPayload);
            console.log('Payment status response:', paymentResponse.data);
            paymentStatus = paymentResponse?.data?.PaymentStatus || null;
            console.log('Payment status fetched:', paymentStatus);
          } catch (error) {
            console.warn('Failed to fetch payment status:', error);
          }
        }
        }

        // Add KYC, doc signing, and payment status to dashboard data
        dashboardData.kycStatus = kycStatus;
        dashboardData.agreementStatus = docSigningStatus;
        dashboardData.paymentStatus = paymentStatus;

        this.dashboardData = dashboardData;
        // console.log('Final dashboardData with statuses:', JSON.stringify(dashboardData));
        sessionStorage.setItem("dashboardData", JSON.stringify(dashboardData));

        // Load financial statement if valid customerId exists
        if (isValidCustomerId) {
          try {
            await Promise.all([
              this.loadFinancialStatement(customerId),
              this.getRecommendationSummary('customer_goals')
            ]);
            console.log('Financial statement loaded for customer:', customerId);
          } catch (error) {
            console.warn('Failed to load financial statement:', error);
            // Don't throw error - continue with dashboard load even if FS fails
          }
        }

        // Determine navigation based on profile completion, KYC status, payment status, and risk assessment completion
        console.log('Auto-navigation check - customerId:', customerId, 'kycStatus:', kycStatus, 'paymentStatus:', paymentStatus);

        const portfolioTargetAlloc = dashboardData.portfolioTargetAlloc || [];
        const hasCompletedRiskAssessment = Array.isArray(portfolioTargetAlloc) && portfolioTargetAlloc.length > 0;

        console.log('Risk assessment status - portfolioTargetAlloc:', portfolioTargetAlloc, 'hasCompleted:', hasCompletedRiskAssessment);

        // Only navigate if skipNavigation is false
        if (!skipNavigation) {
          // Set appropriate tab based on conditions in the correct flow order:
          // 1. Profile → 2. KYC → 3. Agreement Signing → 4. Payment → 5. Risk Assessment → 6. Dashboard
          if (!isValidCustomerId) {
            console.log('No valid customerId - navigating to ManageProfile');
            this.setTabView('ManageProfile');
          } else if (!kycStatus || (kycStatus !== 'Done' && kycStatus !== 'Verified')) {
            console.log('KYC not completed - navigating to KYCVerification');
            this.setTabView('KYCVerification');
          } else if (kycStatus === 'Done' || kycStatus === 'Verified') {
            // KYC is verified - check if agreement is signed AND verified (from backend API)
            const agreementVerified = docSigningStatus === 'Verified';

            if (!agreementVerified) {
              console.log('KYC verified but agreement not verified - navigating to AgreementSigning');
              this.setTabView('AgreementSigning');
            } else if (paymentStatus === 'Initiated' || !paymentStatus) {
              console.log('Agreement verified, payment not completed - navigating to PaymentQRCode');
              this.setTabView('PaymentQRCode');
            } else if (paymentStatus === 'Paid') {
              console.log('Payment Paid, pending verification - navigating to PaymentStatus');
              this.setTabView('PaymentStatus');
            } else if (paymentStatus === 'Verified') {
              // Update DB with verification statuses
              if (!hasVerifiedStatuses) {
                await this.updateCustomerRecord({
                  kycStatus: 'Verified',
                  agreementStatus: 'Verified',
                  paymentStatus: 'Verified'
                });
              }

              if (!hasCompletedRiskAssessment) {
                console.log('Payment Verified but risk assessment not done - navigating to MyModelPortfolio');
                this.setTabView('MyModelPortfolio');
              } else {
                console.log('Payment Verified and risk assessment done - navigating to Dashboard');
                this.setTabView('Dashboard');
              }
            } else {
              console.log('Unknown payment status - navigating to PaymentQRCode');
              this.setTabView('PaymentQRCode');
            }
          } else {
            console.log('Unknown status - navigating to appropriate step');
            this.setTabView('ManageProfile');
          }
        } else {
          console.log('Navigation skipped - staying on current tab:', this.userTab);
        }

        this.setDashboardLoading(false);
        // Turn off profile check loading after tab is determined
        this.isProfileCheckLoading = false;
      } catch (error) {
        this.setDashboardLoading(false);
        this.isProfileCheckLoading = false; // Turn off profile check loading on error too
        const errorMessage = error?.message || error?.toString() || 'Failed to load dashboard data';
        this.fireAlert("Error", errorMessage);
      }
    },

    async loadFinancialStatement(payload) {
      this.setDashboardLoading(true);
      try {
        const response = await ApiService.postData(apis.getFinancialStatement, {
          customerId: payload,
        });
        let data = response.data.myPersonalFinancialStatement;
        this.financialStatement = data;
        console.log('financial statement data:', this.financialStatement)
        sessionStorage.setItem("financialStatement", JSON.stringify(data));
        this.setDashboardLoading(false);
      } catch (error) {
        this.setDashboardLoading(false);
        this.fireAlert("Error", error.message);
      }
    },

    async loadGoalSummary(payload = null) {
      this.setDashboardLoading(true);
      try {
        // Merge provided payload with default dashboardData values
        const requestPayload = {
          customerId: this.dashboardData.customerId,
          portfolioId: this.dashboardData.portfolioId,
          ...payload
        };

        const response = await ApiService.postData(apis.getGoalSummary, requestPayload);
        let data = response.data;
        this.goalSummary = data;
        console.log('goal summary data fetched', data);
        sessionStorage.setItem("goalSummary", JSON.stringify(data));
        console.log('goal summary data:', this.goalSummary);
        this.setDashboardLoading(false);
      } catch (error) {
        this.setDashboardLoading(false);
        this.fireAlert("Error", error.message);
      }
    },
    async getScreenerData() {
      this.setDashboardLoading(true);
      console.log('fetching screener data');
      try {
        const response = await ApiService.postData(apis.getScreenerData, {
          screener_query_name: "Default Screener"
        });
        console.log('screener data fetched', response);
        let data = response.data.screenerData;
        this.screenerData = data;
        console.log('screener data fetched', data);
        sessionStorage.setItem("screenerData", JSON.stringify(data));
        this.setDashboardLoading(false);
      } catch (error) {
        this.setDashboardLoading(false);
        this.fireAlert("Error", error.message);
      }
    },
    async getPortfolioAnalyticsData() {
      this.setDashboardLoading(true);
      console.log('fetching portfolio analytics data');
      try {
        const response = await ApiService.postData(apis.getPortfolioAnalyticsData, {
          customerId: this.dashboardData.customerId,
          portfolioId: this.dashboardData.portfolioId,
        });
        let data = response.data.portfolioAnalyticsData;
        this.portfolioAnalyticsData = data;
        console.log('portfolio analytics data fetched', data);
        sessionStorage.setItem("portfolioAnalyticsData", JSON.stringify(data));
        this.setDashboardLoading(false);
      } catch (error) {
        this.setDashboardLoading(false);
        this.fireAlert("Error", error.message);
      }
    },

    async getRiskAssessment(payload, skipNavigation = false) {
      // SEBI Compliance Check: Prevent API calls for unauthenticated users
      const isDashboardMode = this.user && this.dashboardData?.customerId;
      const isValidCustomerId = this.dashboardData?.customerId &&
                                this.dashboardData.customerId !== "None" &&
                                this.dashboardData.customerId !== "" &&
                                !isNaN(parseInt(this.dashboardData.customerId));

      if (!isDashboardMode || !isValidCustomerId) {
        console.log('Demo mode - skipping actual API call for SEBI compliance');
        // Return null to signal that dummy data should be used
        return { isDemoMode: true };
      }

      this.isRiskAssessmentLoading = true;
      console.log('submitting risk assessment', payload);
      try {
        // Check if this is a rerun (has riskProfile, goal_return, etc.) or initial assessment
        const isRerun = payload.riskProfile && payload.goal_return !== undefined;

        let requestBody;
        if (isRerun) {
          // For rerun, send the direct payload with additional fields
          requestBody = {
            ...payload,
            customerId: this.dashboardData?.customerId,
            timestamp: new Date().toISOString(),
          };
        } else {
          // For initial assessment, use the original format
          requestBody = {
            answers: payload.answers,
            totalScore: payload.totalScore,
            riskAversionCoefficient: payload.riskAversionCoefficient,
            timestamp: new Date().toISOString(),
            customerId: this.dashboardData?.customerId,
          };
        }

        const response = await ApiService.postData(apis.getRiskAssessment, requestBody);
        let data = response.data;
        this.riskAssessmentData = data;
        console.log('is rerun:', isRerun);
        console.log('risk assessment completed', data);

        // Reload dashboard data to update portfolioTargetAlloc immediately
        await this.loadDashboardData(this.user.email, skipNavigation);
        console.log('Dashboard data reloaded after risk assessment');

        this.isRiskAssessmentLoading = false;
        return data;
      } catch (error) {
        this.isRiskAssessmentLoading = false;
        this.fireAlert("Error", error.message);
        throw error;
      }
    },

    async setGoal(paylaod) {
      // Validate customerId before proceeding
      const customerId = this.dashboardData?.customerId;
      const isValidCustomerId = customerId &&
                                customerId !== "None" &&
                                customerId !== "" &&
                                !isNaN(parseInt(customerId));

      if (!isValidCustomerId) {
        console.error('Invalid customerId in setGoal:', customerId);
        this.fireAlert("Error", "Profile not found. Please complete your profile first.");
        this.setDashboardLoading(false);
        // Reload dashboard data to get latest state
        if (this.user?.email) {
          await this.loadDashboardData(this.user.email);
        }
        return false;
      }

      const updatedPayload = {
        ...paylaod,
        customerId: customerId,
      };
      this.setDashboardLoading(true);
      try {
        const response = await ApiService.postData(
          apis.postGoalData,
          updatedPayload
        );
        // Reload goal summary
        await this.loadGoalSummary();

        // Optionally reload dashboard data to ensure sync (but don't trigger navigation)
        // This ensures customerId and other critical data remain intact
        const currentTab = this.userTab; // Save current tab
        await this.loadDashboardData(this.user.email);
        this.userTab = currentTab; // Restore tab to prevent unwanted navigation

        //this.fireAlert("Success", response.data.manageGoalsMessage);
        this.setDashboardLoading(false);
        return true;
      } catch (error) {
        this.fireAlert("Error", error.message);
        this.setDashboardLoading(false);
        return false;
      }
    },

    async getMutualFundData(id) {
      const payload = {
        mutualFundId: id,
      };
      console.log('fetching mutual fund data', payload);
      const response = await ApiService.postData(
        apis.getMutualFundData,
        payload
      );
      console.log('response', response);
      //this.fireAlert("Success", "Mutual Fund Data Loaded");
      console.log('mutual fund data fetched', response.data.mutualFundsData);
      return response.data.mutualFundsData;

    },
    async getRecommendationSummary(dataType = null) {
      const payload = {
        customerId: this.dashboardData.customerId,
      };

      // Add dataType to payload if provided
      if (dataType) {
        payload.dataType = dataType;
      }

      console.log('fetching recommendation summary', payload);
      const response = await ApiService.postData(
        apis.getRecommendationSummary,
        payload
      );
      if (dataType=="customer_goals") {
        const goalSummary = response.data?.responseData?.recommendationData?.goalSummary;
        // Update store with fresh data from backend (could be null if deleted)
        this.goalSummary = goalSummary || null;

        if (goalSummary) {
          sessionStorage.setItem("goalSummary", JSON.stringify(goalSummary));
          console.log('goal summary fetched and stored in session at dashboardload', this.goalSummary);
        } else {
          // Backend returned null/undefined - clear stale session data
          sessionStorage.removeItem("goalSummary");
          console.log('goal summary not found in backend - cleared stale session data');

          // Check if customer has goals that need recalculation
          const custGoals = this.dashboardData?.custGoals || [];
          if (Array.isArray(custGoals) && custGoals.length > 0) {
            console.log('Customer has goals but no summary - triggering loadGoalSummary for recalculation');
            try {
              await this.loadGoalSummary();
            } catch (error) {
              console.warn('Failed to recalculate goal summary:', error);
            }
          }
        }
      }
      console.log('recommendation summary fetched', response.data);
      return response.data;
    },
    async publishRecommendationEvent() {
      // Always fetch fresh goal summary from backend to avoid stale data issues
      console.log('Fetching fresh goal summary from backend for recommendation event...');
      try {
        await this.getRecommendationSummary('customer_goals');
      } catch (error) {
        console.warn('Failed to fetch fresh goal summary, will proceed with cached data or empty object:', error);
      }

      // Handle null, undefined, or empty string goalSummary by converting to empty object
      const goalSummaryData = (this.goalSummary === "" || this.goalSummary === null || this.goalSummary === undefined)
        ? {}
        : this.goalSummary;

      const payload = {
        customerId: this.dashboardData.customerId,
        portfolioId: this.dashboardData.portfolioId,
        myPersonalFinancialStatement: this.financialStatement || {},
        portfolioAnalyticsData: this.portfolioAnalyticsData || {},
        goalSummary: goalSummaryData,
      };
      console.log('publishing recommendation event with fresh data', payload);
      const response = await ApiService.postData(
        apis.publishRecommendationEvent,
        payload
      );
      console.log('recommendation event published', response.data);
      return response.data;
    },

    // Clear goal summary cache - useful after goal deletion or to force refresh
    clearGoalSummaryCache() {
      console.log('Clearing goal summary cache from store and session storage');
      this.goalSummary = null;
      sessionStorage.removeItem("goalSummary");
    },

    async getModelPortfolioHistReturns(assetClassesList) {
      const payload = {
        assetClasses: assetClassesList,
      };
        const response = await ApiService.postData(apis.getModelPortfolioHistReturns, payload);
        console.log('model portfolio historical returns fetched', response.data);
        return response.data;

    },

    async getStocksData(name) {
      const payload = {
        stockName: name,
      };
        const response = await ApiService.postData(apis.getStockData, payload);
        //this.fireAlert("Success", "Stock Data Loaded");
        return response.data.stockData;

    },
    async addStock(paylaod, skipDashboardReload = false) {
      const updatedPayload = {
        ...paylaod,
        customerId: this.dashboardData.customerId,
        portfolioId: this.dashboardData.portfolioId,
      };
      this.setDashboardLoading(true);
      try {
        const response = await ApiService.postData(
          apis.postStockData,
          updatedPayload
        );
        if (!skipDashboardReload) {
          await this.loadDashboardData(this.user.email);
          console.log('dashboard & financial statement reloaded');
        }
        //this.fireAlert("Success", response.data.message);
        this.setDashboardLoading(false);
        return true;
      } catch (error) {
        this.fireAlert("Error", error.message);
        this.setDashboardLoading(false);
        return false;
      }
    },
    async addMutualFund(payload, skipDashboardReload = false) {
      const updatedPayload = {
        ...payload,
        customerId: this.dashboardData.customerId,
        portfolioId: this.dashboardData.portfolioId,
      };
      this.setDashboardLoading(true);
      try {
        const response = await ApiService.postData(
          apis.addMutualFund,
          updatedPayload
        );
        if (!skipDashboardReload) {
          await this.loadDashboardData(this.user.email);
          console.log('dashboard & financial statement reloaded');
        }
        //this.fireAlert("Success", response.data.message);
        this.setDashboardLoading(false);
        return true;
      } catch (error) {
        this.fireAlert("Error", error.message);
        this.setDashboardLoading(false);
        return false;
      }
    },
    async addFixedIncomeData(payload, skipDashboardReload = false) {
      const updatedPayload = {
        ...payload,
        customerId: this.dashboardData.customerId,
        portfolioId: this.dashboardData.portfolioId,
      };
      console.log('adding fixed income data', updatedPayload);
      this.setDashboardLoading(true);
      try {
        const response = await ApiService.postData(
          apis.addFixedIncomeData,
          updatedPayload
        );
        console.log('fixed income added response:', response.data);
        if (!skipDashboardReload) {
          await this.loadDashboardData(this.user.email);
          console.log('dashboard & financial statement reloaded');
        }
        this.setDashboardLoading(false);
        return true;
      } catch (error) {
        this.fireAlert("Error", error.message);
        this.setDashboardLoading(false);
        return false;
      }
    },
    async addOtherInvestments(payload, skipDashboardReload = false) {
      const updatedPayload = {
        ...payload,
        customerId: this.dashboardData.customerId,
        portfolioId: this.dashboardData.portfolioId,
      };
      console.log('adding other investments data', updatedPayload);
      this.setDashboardLoading(true);
      try {
        const response = await ApiService.postData(
          apis.addOtherInvestments,
          updatedPayload
        );
        console.log('other investments added response:', response.data);
        if (!skipDashboardReload) {
          await this.loadDashboardData(this.user.email);
          console.log('dashboard & financial statement reloaded');
        }
        this.setDashboardLoading(false);
        return true;
      } catch (error) {
        this.fireAlert("Error", error.message);
        this.setDashboardLoading(false);
        return false;
      }
    },
    async updateFinancialStatement(payload) {
      const updatedPayload = {
        ...payload,
        customerId: this.dashboardData.customerId
      };
      this.setDashboardLoading(true);
      try {
        const response = await ApiService.postData(apis.updateFinancialStatement, updatedPayload);
        console.log('financial statement updated', response.data);

        // Reload financial statement to refresh cache
        await this.loadFinancialStatement(this.dashboardData.customerId);

        this.fireAlert('Success', 'Financial statement updated successfully');
        return response.data;
      } catch (error) {
        this.fireAlert("Error", error.message);
        return false;
      } finally {
        this.setDashboardLoading(false);
      }
    },
    async getBenchmarkData(benchmarkName) {
      const payload = {
        benchmarkName: benchmarkName,
      };

      const response = await ApiService.postData(apis.getBenchmarkData, payload);
      //this.fireAlert("Success", "Benchmark Data Loaded");
      return response.data;

    },
    async getStockValuationParams(stockName) {
      const payload = {
        stockName: stockName,
      };

      const response = await ApiService.postData(apis.getStockValuationParams, payload);
      console.log('stock valuation params fetched', response.data);
      return response.data;

    },
    async getStockTechData(stockName) {
      const payload = {
        stockName: stockName,
      };

      const response = await ApiService.postData(apis.getStockTechData, payload);
      console.log('stock tech data fetched', response.data);
      return response.data;

    },
    async getMutualFundRecommender(payload) {
      const mfPayload = {
        ...payload,
        customerId: this.dashboardData.customerId,
      };

      const response = await ApiService.postData(apis.getMutualFundRecommender, mfPayload);
      //this.fireAlert("Success", "Mutual Fund Recommender Data Loaded");
      console.log('mutual fund recommender data fetched', response.data);
      return response.data;

    },
    async getAssetAllocationDebugInfo() {
      const payload = {
        customerId: this.dashboardData.customerId,
      };

      const response = await ApiService.postData(apis.getAssetAllocationDebugInfo, payload);
      console.log('asset allocation debug info fetched', response.data);
      return response.data;

    },
    async createUserProfile(payload) {
      this.setAuthLoading(true);
      const userProfilePayload = {
        ...payload,
        userEmail: this.user.email,
      };

      try {
        console.log('userProfilePayload:', userProfilePayload);
        const response = await ApiService.postData(apis.createUserProfile, userProfilePayload);
        //this.fireAlert("Success", "User Profile Created");
        console.log('user profile created', response.data);
        this.setAuthLoading(false);
        return response.data;
      } catch (error) {
        this.setAuthLoading(false);
        this.fireAlert("Error", error.message);
        throw error;
      }
    },

    async getBlogsVideos(payload) {
      console.log('fetching blogs & videos data with payload:', payload);
      const blogsVideosPayload = {
        ...payload,
        customerId: this.dashboardData.customerId,
      };

      const response = await ApiService.postData(apis.getBlogsVideos, blogsVideosPayload);
      console.log('blogs & videos data fetched', response.data);
      //this.fireAlert("Success", "Blogs & Videos Data Loaded");
      console.log('blogs & videos data:', response.data);
      return response.data;

    },
    async getPortfolioAttributionData(portfolioId) {
      // Use cached attribution data if available
      if (this.portfolioAttributionData) {
        return this.portfolioAttributionData;
      }

      const payload = {
        portfolioId,
        stocksOwned: this.dashboardData?.stocksOwned || [],
        portfolioTargetAlloc: this.dashboardData?.portfolioTargetAlloc || []
      };

      const response = await ApiService.postData(apis.getPortfolioAttributionData, payload);
      const data = response?.data || {};
      this.portfolioAttributionData = data;
      console.log('portfolio attribution data fetched', this.portfolioAttributionData);
      return data;
    },
    async getStocksMFCurrentPrice(stockNSE, requestFor) {

      const payload = {
        symbol: stockNSE,
        requestFor: requestFor
      };
        const response = await ApiService.postData(apis.getStockMFCurrentPrice, payload);
        //this.fireAlert("Success", "Stock/MF Current Price Data Loaded");

        return response.data.priceData;
    },
    async authenticateAndRedirectToAA({ phoneNumber, pan }) {
      const payload = {
        mobileNumber: phoneNumber,
        pan: pan
      };
        const response = await ApiService.postData(apis.authenticateAndRedirectToAA, payload);
        // this.fireAlert("Success", "Authentication Successful");
        console.log('AA redirection response', response.data);
        return response.data;
    },
    async logCustomerSupportTicket(payload) {
      const ticketPayload = {
        ...payload,
        EmailId: this.user.email,
        PhoneNumber: this.dashboardData?.phoneNumber || '',
      };

      const response = await ApiService.postData(apis.logCustomerSupportTicket, ticketPayload);
      console.log('customer support ticket logged', response.data);
      return response.data;
    },
    async getCustomerSupportTickets() {
      const payload = {
        EmailId: this.user.email,
      };

      const response = await ApiService.postData(apis.getCustomerSupportTickets, payload);
      console.log('customer support tickets fetched', response.data);
      return response.data;
    },

    async updateCustomerPaymentStatus(payload) {
      const updatedPayload = {
        PhoneNumber: this.dashboardData?.phoneNumber || '',
        CustomerId: this.dashboardData.customerId,
        ...payload,
      };

      console.log('Updating payment status with payload:', updatedPayload);
      const response = await ApiService.postData(apis.updateCustomerPaymentStatus, updatedPayload);
      console.log('customer payment status updated', response.data);

      // Reload dashboard data to refresh payment status, subscription type, and validThruDate
      await this.loadDashboardData(this.user.email);

      return response.data;
    },
    async getCustomerPaymentStatus(payload) {
      const updatedPayload = {
        PhoneNumber: this.dashboardData?.phoneNumber || '',
        CustomerId: this.dashboardData.customerId,
        ...payload,
      };

      const response = await ApiService.postData(apis.getCustomerPaymentStatus, updatedPayload);
      console.log('customer payment status fetched', response.data);
      return response.data;
    },

    async getPANKRAStatus(payload) {
      const response = await ApiService.postData(apis.getPANKRAStatus, payload);
      console.log('PANKRA status fetched', response.data);
      return response.data;
    },
    async createKYCRequests(payload) {
      const response = await ApiService.postData(apis.createKYCRequests, payload);
      console.log('KYC requests created', response.data);
      return response.data;
    },
    async getKYCRequestStatus(payload) {
      const response = await ApiService.postData(apis.getKYCRequestStatus, payload);
      console.log('KYC request status fetched', response.data);
      return response.data;
    },
    async updateKYCRequestStatus(payload) {
      const response = await ApiService.postData(apis.updateKYCRequestStatus, payload);
      console.log('KYC request status updated', response.data);
      return response.data;
    },
    async createDocSigningRequest(payload) {
      const updatedPayload = {
        PhoneNumber: this.dashboardData?.phoneNumber || '',
        CustomerId: this.dashboardData?.customerId || '',
        Name: `${this.dashboardData?.firstNm || ''} ${this.dashboardData?.lastNm || ''}`.trim(),
        PAN: this.dashboardData?.pan || '',
        Address: this.dashboardData?.address || '',
        ...payload,
      };

      const response = await ApiService.postData(apis.createDocSigningRequest, updatedPayload);
      console.log('document signing request created', response.data);
      return response.data;
    },

    async markAgreementAsSigned() {
      const customerId = this.dashboardData?.customerId;
      const phoneNumber = this.dashboardData?.phoneNumber;

      if (customerId && phoneNumber) {
        // Update in localStorage first
        localStorage.setItem(`agreement_signed_${customerId}`, 'true');
        console.log('Agreement marked as signed in localStorage for customer:', customerId);

        // Update in backend
        try {
          const payload = {
            PhoneNumber: phoneNumber,
            CustomerId: customerId,
            Status: 'signed'
          };
          const response = await ApiService.postData(apis.updateDocSigningStatus, payload);
          console.log('Document signing status updated in backend:', response.data);

          // Reload dashboard data to update navigation
          await this.loadDashboardData(this.user.email);
        } catch (error) {
          console.error('Failed to update doc signing status in backend:', error);
          // Don't throw error - localStorage update is sufficient for now
        }
      }
    },

    async getDocSigningStatus() {
      const customerId = this.dashboardData?.customerId;
      const phoneNumber = this.dashboardData?.phoneNumber;

      if (!customerId || !phoneNumber) {
        return { success: false, data: { data: { DocSigningStatus: null } } };
      }

      try {
        const payload = {
          PhoneNumber: phoneNumber,
          CustomerId: customerId
        };
        const response = await ApiService.postData(apis.getDocSigningStatus, payload);
        console.log('Document signing status fetched from backend:', response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch doc signing status:', error);
        return { success: false, data: { data: { DocSigningStatus: null } } };
      }
    },
    async getGoalAssetAllocation(payload) {
      const response = await ApiService.postData(apis.getGoalAssetAllocation, payload);
      console.log('Goal asset allocation fetched successfully', response.data);
      return response.data;
    },
    async getGlobalInvestOptions(payload) {
      const response = await ApiService.postData(apis.getGlobalInvestOptions, payload);
      console.log('Global investment options fetched successfully', response.data);
      return response.data;
    },
      async getGlobalSecuritiesData(payload) {
      const response = await ApiService.postData(apis.getGlobalSecuritiesData, payload);
      console.log('Global securities data fetched successfully', response.data);
      return response.data;
    },
    async getGlobalDashboardData(customerId) {
      try {
        const payload = {
          customer_id: customerId
        };
        console.log('fetching global dashboard data with payload:', payload);
        const response = await ApiService.postData(apis.getGlobalDashboardData, payload);
        console.log('Global dashboard data fetched successfully', response.data);
        // Ensure we return a valid structure even if response.data is undefined
        return response.data || {
          customerGlobalHoldings: {
            customerId: customerId,
            holdings: [],
            metadata: {}
          }
        };
      } catch (error) {
        console.error('Error fetching global dashboard data:', error);
        this.fireAlert("Error", error.message || "Failed to fetch global dashboard data");
        // Return empty structure to prevent undefined errors
        return {
          customerGlobalHoldings: {
            customerId: customerId,
            holdings: [],
            metadata: {}
          }
        };
      }
    },
    async updateGlobalDashboardData(payload) {
      const response = await ApiService.postData(apis.updateGlobalDashboardData, payload);
      console.log('Global dashboard data updated successfully', response.data);
      return response.data;
    },
    async updateCustomerRecord(payload) {
      // Add user context from dashboardData if not already provided
      const enrichedPayload = {
        ...payload,
        customerId: this.dashboardData?.customerId || ''
      }

      console.log('Updating Customer Record with payload:', enrichedPayload)
      const response = await ApiService.postData(apis.updateCustomerRecord, enrichedPayload)
      console.log('Update Customer Record response', response.data)
      return response.data
    },
    async orderThroughKiteConnect(payload) {
      // Add user context from dashboardData if not already provided
      const enrichedPayload = {
        ...payload,
        mobileNumber: this.dashboardData?.phoneNumber || '',
        pan: this.dashboardData?.pan || '',
        email: this.user?.email || '',
        portfolioId: this.dashboardData?.portfolioId || ''
      }

      console.log('Sending order through Kite Connect with payload:', enrichedPayload)
      const response = await ApiService.postData(apis.orderThroughKiteConnect, enrichedPayload)
      console.log('Order through Kite Connect response', response.data)
      return response.data
    },

    // SIP Portfolio Allocation Methods
    async saveSIPAllocation(sipAllocationData) {
      try {
        // Ensure dates are in YYYY-MM-DD format
        const today = new Date().toISOString().split('T')[0]

        const payload = {
          customerId: sipAllocationData.customerId || this.dashboardData?.customerId,
          sipTier: String(sipAllocationData.sipTier),
          tierRange: sipAllocationData.tierRange,
          allocations: sipAllocationData.allocations.map(allocation => ({
            fundName: allocation.fundName,
            assetClass: allocation.assetClass,
            allocationPercentage: allocation.allocationPercentage,
            monthlySIP: allocation.monthlySIP
          })),
          questionnaireAnswers: sipAllocationData.sipTier === '3' ? (sipAllocationData.questionnaireAnswers || {}) : null,
          createdAt: sipAllocationData.createdAt || today,
          lastModified: today
        }

        console.log('Saving SIP allocation with payload:', payload)
        const response = await ApiService.postData(apis.saveSIPTargetAllocation, payload)
        console.log('SIP allocation saved successfully:', response.data)

        // Update dashboard data with new SIP allocation
        if (this.dashboardData) {
          this.dashboardData.sipPortfolioAllocation = response.data.sipAllocation || response.data
          sessionStorage.setItem("dashboardData", JSON.stringify(this.dashboardData))
        }

        return response.data
      } catch (error) {
        console.error('Error saving SIP allocation:', error)
        this.fireAlert('Error', 'Failed to save SIP allocation')
        throw error
      }
    },

    async loadSIPAllocation(customerId) {
      try {
        const payload = {
          customerId: customerId || this.dashboardData?.customerId
        }

        console.log('Loading SIP allocation with payload:', payload)
        const response = await ApiService.postData(apis.loadSIPTargetAllocation, payload)
        console.log('SIP allocation loaded successfully:', response.data)

        // Check if response has data or if it's a "no records found" response
        if (response.data && response.data.data === null) {
          console.log('No SIP allocation found for customer')
          return null
        }

        // Verify dates are in YYYY-MM-DD format (validation)
        if (response.data && response.data.data) {
          const data = response.data.data
          if (data['created-at']) {
            console.log('SIP allocation created-at:', data['created-at'])
          }
          if (data['last-modified']) {
            console.log('SIP allocation last-modified:', data['last-modified'])
          }

          // Transform the response data to match expected format
          return {
            customerId: data.CustomerId,
            sipTier: data['sip-tier'],
            tierRange: data['tier-range'],
            allocations: data['fund-allocations'] || [],
            questionnaireAnswers: data['profile-responses'] || null,
            createdAt: data['created-at'],
            lastModified: data['last-modified']
          }
        }

        return response.data
      } catch (error) {
        console.error('Error loading SIP allocation:', error)
        return null
      }
    }
  },

  getters: {
    getuserData: (state) => state.user,
    getDashboardData: (state) => state.dashboardData,
  },
});
